// ============================================================================
// DRAW BUBBLE SORT CLASSIC PASS-GRID
// Classic YouTube-style visualization: each pass is a separate row, array
// elements stay in fixed horizontal positions, and swap/no-swap arrows are
// drawn above the active comparison pair.
// ============================================================================

import { type ExecutionStep, StepType } from '../../../../core/types/rust-engine-interfaces';

const COLORS = {
  blockDefault: 'rgba(59, 130, 246, 0.85)',
  blockDefaultBorder: '#60a5fa',
  blockCompare: 'rgba(251, 191, 36, 0.85)',
  blockCompareBorder: '#fbbf24',
  blockSwap: 'rgba(248, 113, 113, 0.85)',
  blockSwapBorder: '#f87171',
  blockSorted: 'rgba(34, 197, 94, 0.85)',
  blockSortedBorder: '#22c55e',
  text: '#ffffff',
  arrowSwap: '#f87171',
  arrowNoSwap: '#22c55e',
  label: '#cbd5e1',
  passLabelActive: '#f59e0b',
  passLabelHistory: '#64748b',
};

const LAYOUT = {
  padX: 28,
  padY: 65,
  blockH: 26,
  rowGap: 8,
  gapX: 4,
  minBlockW: 22,
  maxBlockW: 44,
  borderRadius: 4,
  fontSize: 12,
  fontSizeSmall: 9,
  arrowHeight: 16,
  arrowheadSize: 5,
  labelOffset: 8,
  bottomLabelY: 4,
};

interface PassSnapshot {
  passIdx: number;
  values: number[];
  sortedTail: number;
}

export function drawBubbleSort(
  ctx: CanvasRenderingContext2D,
  currentStep: ExecutionStep,
  W: number,
  H: number,
  allSteps?: ExecutionStep[],
  stepIndex?: number,
): void {
  if (currentStep.payload.type !== 'array') return;
  const items = currentStep.payload.items;
  const values = items.map(i => Number(i.value) || 0);
  const n = values.length;
  if (n === 0) return;

  const meta = currentStep.payload.metadata;
  const explanation = currentStep.metadata?.explanation ?? '';
  const compareIndices = meta.compareIndices ?? [];
  const swappedIndices = meta.swappedIndices ?? [];
  const sortedCount = (meta.sortedIndices ?? []).length;
  const stepType = currentStep.stepType;
  const currentPass = parsePassInfo(explanation).currentPass;

  const { padX, padY, blockH, rowGap, gapX, minBlockW, maxBlockW, borderRadius } = LAYOUT;
  const availableW = W - padX * 2;

  const blockW = Math.max(minBlockW, Math.min(maxBlockW, (availableW - (n - 1) * gapX) / n));
  const totalRowW = n * blockW + (n - 1) * gapX;
  const startX = padX + Math.max(0, (availableW - totalRowW) / 2);

  // Build pass snapshots from allSteps history (completed passes only).
  const snapshots = buildPassSnapshots(allSteps, stepIndex);

  // Reserve 1 row for the current pass. Show as many history rows as fit.
  const maxHistoryRows = Math.max(0, Math.floor((H - padY * 2) / (blockH + rowGap)) - 1);
  const historyToShow = snapshots.slice(-maxHistoryRows);
  const totalRows = historyToShow.length + 1;

  // Draw historical passes.
  for (let r = 0; r < historyToShow.length; r++) {
    const snap = historyToShow[r]!;
    const rowY = padY + r * (blockH + rowGap);
    drawPassRow(ctx, snap.values, startX, rowY, blockW, blockH, gapX, borderRadius, null, [], snap.sortedTail, snap.passIdx, false, StepType.Comparison);
  }

  // Draw current pass (always last row).
  {
    const rowY = padY + (totalRows - 1) * (blockH + rowGap);
    const pair = compareIndices.length >= 2
      ? [compareIndices[0]!, compareIndices[1]!] as [number, number]
      : null;
    drawPassRow(ctx, values, startX, rowY, blockW, blockH, gapX, borderRadius, pair, swappedIndices, sortedCount, currentPass, true, stepType);
  }

  // Bottom label.
  ctx.font = `bold ${LAYOUT.fontSizeSmall}px monospace`;
  ctx.fillStyle = COLORS.label;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'bottom';
  ctx.fillText('Bubble Sort: each row = one pass, arrows show swap / no swap', padX, H - LAYOUT.bottomLabelY);
}

function buildPassSnapshots(
  allSteps: ExecutionStep[] | undefined,
  stepIndex: number | undefined,
): PassSnapshot[] {
  if (!allSteps || stepIndex === undefined) return [];

  const snapshots: PassSnapshot[] = [];
  const snapshotMap = new Map<number, PassSnapshot>();
  let currentPass = 1;

  // Walk through steps up to current index, tracking the current pass number.
  // Pass number is only present in Decision and "pass done" explanation strings.
  // For Comparison/Swap steps, we inherit the most recently seen pass number.
  for (let i = 0; i <= stepIndex && i < allSteps.length; i++) {
    const step = allSteps[i]!;
    if (step.payload.type !== 'array') continue;

    const expl = step.metadata?.explanation ?? '';
    const parsed = parsePassInfo(expl);
    if (parsed.currentPass > 1 || expl.toLowerCase().includes('pass')) {
      currentPass = parsed.currentPass;
    }

    const stepValues = step.payload.items.map(it => Number(it.value) || 0);
    const sortedTail = (step.payload.metadata.sortedIndices ?? []).length;

    const existing = snapshotMap.get(currentPass);
    if (existing) {
      existing.values = stepValues;
      existing.sortedTail = sortedTail;
    } else {
      const snap: PassSnapshot = { passIdx: currentPass, values: stepValues, sortedTail };
      snapshotMap.set(currentPass, snap);
      snapshots.push(snap);
    }
  }

  // Remove the current (last) pass — it will be drawn from currentStep.
  if (snapshots.length > 0) {
    snapshots.pop();
  }

  return snapshots;
}

function parsePassInfo(explanation: string): { currentPass: number } {
  const match = explanation.match(/Pass\s+(\d+)/i);
  if (match) return { currentPass: parseInt(match[1]!, 10) };
  return { currentPass: 1 };
}

function drawPassRow(
  ctx: CanvasRenderingContext2D,
  values: number[],
  startX: number,
  rowY: number,
  blockW: number,
  blockH: number,
  gapX: number,
  borderRadius: number,
  pair: [number, number] | null,
  swappedIndices: number[],
  sortedTail: number,
  passIdx: number,
  isCurrentRow: boolean,
  stepType: StepType,
): void {
  const swappedSet = new Set(swappedIndices);

  drawBlocks(ctx, values, startX, rowY, blockW, blockH, gapX, borderRadius, pair, swappedSet, sortedTail, stepType);

  if (pair && isCurrentRow) {
    drawSwapArrow(ctx, startX, pair, blockW, gapX, rowY, swappedSet);
  }

  drawPassLabel(ctx, startX, rowY, blockH, passIdx, isCurrentRow);
}

function drawBlocks(
  ctx: CanvasRenderingContext2D,
  values: number[],
  startX: number,
  rowY: number,
  blockW: number,
  blockH: number,
  gapX: number,
  borderRadius: number,
  pair: [number, number] | null,
  swappedSet: Set<number>,
  sortedTail: number,
  stepType: StepType,
): void {
  const n = values.length;

  for (let i = 0; i < n; i++) {
    const x = startX + i * (blockW + gapX);
    const inPair = pair !== null && (i === pair[0] || i === pair[1]);
    const isSwapped = inPair && swappedSet.has(i) && stepType === StepType.Update;
    const isSorted = i >= n - sortedTail;

    const { fill, border } = resolveBlockColors(isSwapped, inPair, isSorted);

    ctx.save();
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.roundRect(x, rowY, blockW, blockH, borderRadius);
    ctx.fill();

    ctx.strokeStyle = border;
    ctx.lineWidth = inPair ? 2 : 1.5;
    ctx.beginPath();
    ctx.roundRect(x, rowY, blockW, blockH, borderRadius);
    ctx.stroke();

    ctx.fillStyle = COLORS.text;
    ctx.font = `bold ${LAYOUT.fontSize}px monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(values[i]), x + blockW / 2, rowY + blockH / 2);
    ctx.restore();
  }
}

function resolveBlockColors(
  isSwapped: boolean,
  inPair: boolean,
  isSorted: boolean,
): { fill: string; border: string } {
  if (isSwapped) return { fill: COLORS.blockSwap, border: COLORS.blockSwapBorder };
  if (inPair) return { fill: COLORS.blockCompare, border: COLORS.blockCompareBorder };
  if (isSorted) return { fill: COLORS.blockSorted, border: COLORS.blockSortedBorder };
  return { fill: COLORS.blockDefault, border: COLORS.blockDefaultBorder };
}

function drawSwapArrow(
  ctx: CanvasRenderingContext2D,
  startX: number,
  pair: [number, number],
  blockW: number,
  gapX: number,
  rowY: number,
  swappedSet: Set<number>,
): void {
  const ax = startX + pair[0] * (blockW + gapX) + blockW / 2;
  const bx = startX + pair[1] * (blockW + gapX) + blockW / 2;
  const arrowY = rowY - 6;
  const cpY = arrowY - LAYOUT.arrowHeight;
  const swapped = swappedSet.has(pair[0]) || swappedSet.has(pair[1]);
  const color = swapped ? COLORS.arrowSwap : COLORS.arrowNoSwap;
  const label = swapped ? 'swap' : 'no swap';

  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1.5;
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.moveTo(ax, arrowY);
  ctx.quadraticCurveTo((ax + bx) / 2, cpY, bx, arrowY);
  ctx.stroke();

  if (swapped) {
    drawArrowhead(ctx, ax, arrowY, (ax + bx) / 2, cpY, color);
    drawArrowhead(ctx, bx, arrowY, (ax + bx) / 2, cpY, color);
  } else {
    drawArrowhead(ctx, bx, arrowY, (ax + bx) / 2, cpY, color);
  }

  ctx.font = `bold ${LAYOUT.fontSizeSmall}px monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText(label, (ax + bx) / 2, cpY - 2);
  ctx.restore();
}

function drawPassLabel(
  ctx: CanvasRenderingContext2D,
  startX: number,
  rowY: number,
  blockH: number,
  passIdx: number,
  isCurrentRow: boolean,
): void {
  ctx.save();
  ctx.font = `bold ${LAYOUT.fontSizeSmall}px monospace`;
  ctx.fillStyle = isCurrentRow ? COLORS.passLabelActive : COLORS.passLabelHistory;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'middle';
  const labelX = Math.max(LAYOUT.labelOffset + 4, startX - LAYOUT.labelOffset);
  ctx.fillText(`P${passIdx}`, labelX, rowY + blockH / 2);
  ctx.restore();
}

function drawArrowhead(
  ctx: CanvasRenderingContext2D,
  tipX: number,
  tipY: number,
  cpX: number,
  cpY: number,
  color: string,
): void {
  const angle = Math.atan2(tipY - cpY, tipX - cpX);
  const size = LAYOUT.arrowheadSize;
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(tipX, tipY);
  ctx.lineTo(tipX - size * Math.cos(angle - 0.4), tipY - size * Math.sin(angle - 0.4));
  ctx.lineTo(tipX - size * Math.cos(angle + 0.4), tipY - size * Math.sin(angle + 0.4));
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}
